import {defineStore} from "pinia";
import {DonationService} from "@/javascript/services/donation.service";
import {loadStripe} from "@stripe/stripe-js";
import {useI18n} from "vue-i18n";
import {SubscriptionService} from "../services/subscription.service";
import {useUserStore} from "./user";

export const OneTimeDonation = "OneTimeDonation";
export const RecurringDonation = "RecurringDonation";
export const DonationTypes = {
  "donation.one_time_donation": OneTimeDonation, "donation.recurring_donation": RecurringDonation
};

const defaultDonatableImage = "https://d3dwclrxdfwggc.cloudfront.net/production/customer_assets/b18991dcec9b3140236d20e8836fac0be678e55f/original/placeholder-image.png";

export const useDonationSessionStore = defineStore('donationSession', {

  state: () => (
    {
      t: useI18n().t,
      // donationService: new DonationService(),
      donationService: null,
      organizationId: null,
      subscriptionService: null,
      stripeClient: null,
      currentOrganization: {},
      orgProcessor: {},
      donationSession: {
        intent_id: null,
        client_secret: null,
        pub_key: null,
        user_id: null,
        donation_id: null,
        loading: false,
        loaded: false,
        submitting: false,
        completed: true,
        allowRetry: false,
        errors: {},
        successMessage: "",
        redirectUrl: null,
      },
      donationForm: {
        donorEmail: "",
        fName: "",
        lName: "",
        notes: "",
        donationType: OneTimeDonation,
        amount: null,
        frequency: "weekly",
        frequencyOptions: [],
        donationTargets: [],
        selectedTarget: null,
        min: 5.00,
        delayStart: false,
        delayStartDate: null,
        defaultDonatableId: null,
        errors: {},
        tipPercents: [3, 5, 7, "custom"],
        tipMaxPercent: 10,
        selectedTipOption: 5,
        customTipAmount: 0,
        createAccountOnSuccess: true,
        donationPublic: true
      },
      // Array of donations in "gift basket"
      donationCart: [],
      // Stores a single donation for immediate checkout, keeps other donations in cart
      immediateCheckoutDonation: null
    }
  ),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.donationService = new DonationService(id);
        this.subscriptionService = new SubscriptionService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    //* Loads initial donation form, populates recurring donation frequencies, donation targets, etc
    loadOrgDonationForm(organizationId) {
      // Don't reload the same donation session on each page
      if (this.donationSession?.formLoaded && (organizationId === this.organizationId)) {
        return Promise.resolve(true);
      }
      this.setOrganizationId(organizationId);
      let params = new URLSearchParams();
      params.set('organization_id', organizationId);
      return this.donationService.loadDonationForm(params)
        .then(res => {
          this.donationSession = res;
          if (res && res.organization && res.processor) {
            this.currentOrganization = res.organization;
            this.orgProcessor = res.processor;
            this.donationForm.frequencyOptions = res.processor.enabled_subscription_intervals;
            this.setDonationTargets(res.donation_targets);
            this.donationSession.formLoaded = true;

            return true;
          } else {
            return false;
          }
        }).catch(err => {
          console.log('error', err);
          return false;
        });
    },

    /** Donation targets - Campaigns, Funds etc **/
    setDonationTargets(donationTargets) {
      if (donationTargets) {
        this.donationForm.donationTargets = donationTargets;

        // Set the default target.
        let defaultOpt;
        // Check if it's been manually set
        if (this.donationForm.defaultDonatableId) {
          defaultOpt = this.donationForm.donationTargets.find(obj => obj.uuid === this.donationForm.defaultDonatableId);
        }
        // If no default, get org default (either general fund or first campaign of general fund)
        if (!defaultOpt) {
          defaultOpt = this.donationForm.donationTargets.find(obj => obj.org_default);
        }
        // Set the default option
        if (defaultOpt) {
          this.donationForm.selectedTarget = defaultOpt;
        } else {
          console.error('error setting default target');
        }
      }
    },

    /** STRIPE FUNCTIONALITY **/
    getStripeClient() {
      if (!this.stripeClient) {
        return loadStripe(this.donationSession.pub_key)
          .then((client) => {
            this.stripeClient = client;
            return this.stripeClient !== null;
          });
      }
      return true;
    },

    async submitStripePaymentForm(elements) {
      this.donationSession.submitting = true;
      if (await this.getStripeClient()) {
        const token = await this.stripeClient
          .createConfirmationToken({
            elements: elements,
          });
        if (token.confirmationToken?.id) {
          await this.confirmStripeDonation(token.confirmationToken.id);
        } else {
          this.donationSession.errors = {
            error: true,
            message: "payment.errors.generic_payment_error",
            allowRetry: false
          };
        }
      }
    },

    //** Submits the approved payment form and confirms the payment
    //** Handled differently from Helcim because Stripe approves payments through our API
    async confirmStripeDonation(tokenId) {
      this.donationSession.errors = {};

      let params = {
        "intent_id": this.donationSession.intent_id,
        "user_id": this.donationSession.user_id,
        "token_id": tokenId,
        "organization_id": this.organizationId,
        "payment_amount": this.donationSession.payment_amount,
        "create_account": this.donationForm.createAccountOnSuccess,
        "anonymous": !this.donationForm.donationPublic
      };

      await this.finalizeDonation(params);
    },

    //** Confirms the Helcim donation. Helcim completes payments through the frontend only
    //** This will attemtp to update the created donation with the response from Helcim.
    // This will be backed up by webhook callbacks to ensure our data is always in syunc with Helcim
    async confirmHelcimDonation(eventData) {
      this.donationSession.errors = {};

      let params = {
        "intent_id": this.donationSession.checkout_token,
        "user_id": this.donationSession.user_id,
        "donation_id": this.donationSession.donation_id,
        "organization_id": this.organizationId,
        "create_account": this.donationForm.createAccountOnSuccess,
        "anonymous": !this.donationForm.donationPublic,
        "event_data": eventData
      };

      await this.finalizeDonation(params);
    },

    async finalizeDonation(params) {
      let result;
      // Gets the immediate checkout donation or first donation in cart to determine donation type.
      // Can only do one time donation or Subscription separately
      const donation = this.currentActiveDonations[0];

      if (!donation) return;

      if (donation.type === OneTimeDonation) {
        params["donation_ids"] = this.donationSession.donation_ids;
        result = await this.donationService.confirmPayment(params);
      }
      if (donation.type === RecurringDonation) {
        result = await this.subscriptionService.confirmPayment(this.donationSession.subscription_id, params);
      }

      this.donationSession.submitting = false;

      if (result.success) {
        this.donationSession.successMessage = this.t("payment.success");
        if (result.redirect_url) {
          this.donationSession.redirectUrl = result.redirect_url;
        }
        if (result.status && result.status === "pending") {
          this.donationSession.successMessage = this.t("payment.pending");
        }
        if (result.status && result.status === "trialing") {
          this.donationSession.successMessage = this.t("payment.trialing");
        }
        this.donationSession.deviseSessionId = result.session_id;
        this.donationSession.userAuthenticated = result.user_authenticated || false;
        if (result.donation_ids) {
          this.donationSession.donation_ids = result.donation_ids;
        }
        this.donationSession.completed = true;
      } else {
        let message = "payment.errors.generic_payment_error";
        let allowRetry = false;
        if (result.error === "payment_failed") {
          message = "payment.errors.payment_failed";
          allowRetry = true;
        }

        this.donationSession.errors = {
          error: true,
          message: message,
          retry: allowRetry
        };
      }

    },

    async updateFailedDonations(status) {
      // grab an array of all donation_ids for this session and the status that was passed from the button
      let params = {
        donation_ids: this.donationSession.donation_ids,
        status: status
      };
      await this.donationService.updateAbandonedDonations(params);
    },

    addDonationToCart(donation) {
      this.donationCart.push(donation);
    },

    removeDonationFromCart(donation) {
      this.donationCart = this.donationCart.filter(d => d !== donation.object);
    },

    // Immediate checkout with a single donation for 'give now' button
    singleDonationCheckout(donation) {
      this.immediateCheckoutDonation = donation;
    },

    resetImmediateCheckout() {
      this.immediateCheckoutDonation = null;
    },

    emptyCart() {
      this.donationCart = [];
      this.resetState();
    },

    resetState() {
      this.donationForm = {
        donorEmail: "",
        fName: "",
        lName: "",
        notes: "",
        min: 5.00,
        delayStart: false,
        delayStartDate: null,
        defaultDonatableId: null,
        errors: {},
        tipPercents: [3, 5, 7, "custom"],
        tipMaxPercent: 10,
        selectedTipOption: 5,
        customTipAmount: 0,
        // createAccountOnSuccess: true,
        // donationPublic: true
      };
      this.donationSession.intent_id = null;
      this.donationSession.client_secret = null;
      this.donationSession.checkout_token = null;
      this.donationSession.subscription_id = null;
      this.donationSession.loaded = false;
      // reset the donation session
      // this.donationSession.user_id = null;
    },

    /** Loads the payment form for the processor - Stripe or Helcim **/
    loadPaymentForm(customerData = null, userAuthenticated = false) {
      this.donationForm.loading = true;

      //If provided, pre populate customer data
      if (customerData) {
        if (customerData.customer_email) this.donationForm.donorEmail = customerData.customer_email;
        if (customerData.customer_firstName) this.donationForm.fName = customerData.customer_firstName;
        if (customerData.customer_lastName) this.donationForm.lName = customerData.customer_lastName;
      }


      const donation = this.currentActiveDonations[0];

      if (!donation) {
        return;
      }

      let promise = null;
      if (donation.type === OneTimeDonation) {
        promise = this.loadDonationPaymentForm(userAuthenticated);
      }
      if (donation.type === RecurringDonation) {
        promise = this.loadSubscriptionPaymentForm(userAuthenticated);
      }

      if (promise) {
        return promise.then((_) => {
          this.donationForm.loading = false;
          this.donationSession.loaded = true;

          return true;
        });
      }
    },

    loadDonationPaymentForm(userAuthenticated = false) {

      const params = {
        "organization_id": this.organizationId,
        "email": this.donationForm.donorEmail,
        "first_name": this.donationForm.fName,
        "last_name": this.donationForm.lName,
        "payment_subtotal": this.cartSubtotal, // Total sans tip
        "payment_total": this.cartTotal,
        "fee_amount": this.cartTipAmount,
        // This is what triggers if payment info will be shown or not.
        "user_authenticated": userAuthenticated,
        "donations": this.currentActiveDonations.map((donation) => {
          return {
            "donation_type": donation.type,
            "donatable_id": donation.selectedTarget.id,
            "donatable_type": donation.selectedTarget.model,
            "amount": donation.amount,
            "anonymous": !this.donationForm.donationPublic,
            "notes": this.donationForm.notes,
          };
        })
      };

      return this.donationService.initDonationIntent(params)
        .then((result) => {
          if (result.error) {
            throw new Error(result.error);
          } else {
             this.donationSession = result;
            return result;
          }
        });
    },

    loadSubscriptionPaymentForm(userAuthenticated = false) {
      const donation = this.currentActiveDonations[0]; // Can only check out with one subscription at a time
      if (!donation) return;
      const params = {
        "organization_id": this.organizationId,
        "email": this.donationForm.donorEmail,
        "first_name": this.donationForm.fName,
        "last_name": this.donationForm.lName,
        "notes": this.donationForm.notes,
        "payment_total": this.cartTotal,
        "payment_subtotal": this.cartSubtotal, // Total sans tip
        "fee_amount": this.cartTipAmount,
        "amount": donation.amount,
        "frequency": donation.frequency,
        "delay_start": this.donationForm.delayStart,
        "delay_date": this.donationForm.delayStartDate,
        "donatable_id": donation.selectedTarget.id,
        "donatable_type": donation.selectedTarget.model,
        "anonymous": !this.donationForm.donationPublic,
        // This is what triggers if payment info will be shown or not.
        "user_authenticated": userAuthenticated
      };

      return this.subscriptionService.initSubscriptionIntent(params)
        .then((result) => {
          if (result.error) {
            throw new Error(result.error);
          } else {
            this.donationSession = result;
            return result;
          }
        });
    },

    // Validate form elements not related to payment processor
    // Cannibalizing this class a bit for new checkout process, need to clean up a bit
    donationFormValid(donorInfoOnly = true) {
      this.clearFormErrors();

      if (!this.donationForm.donorEmail) {
        this.donationForm.errors['email'] = this.t("donation.errors.email_req");
      } else if (!this.emailValid()) {
        this.donationForm.errors['email'] = this.t("donation.errors.email_invalid");
      }
      const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s.''-]+$/;

      if (!this.donationForm.fName?.trim()) {
        this.donationForm.errors['fName'] = this.t("donation.errors.generic_req");
      } else if (!nameRegex.test(this.donationForm.fName)) {
        this.donationForm.errors['fName'] = this.t("donation.errors.invalid_characters");
      }
      if (!this.donationForm.lName?.trim()) {
        this.donationForm.errors['lName'] = this.t("donation.errors.generic_req");
      } else if (!nameRegex.test(this.donationForm.lName)) {
        this.donationForm.errors['lName'] = this.t("donation.errors.invalid_characters");
      }
      if (this.donationForm.delayStart && !this.donationForm.delayStartDate) {
        this.donationForm.errors['date'] = this.t("donation.errors.start_date");
      }

      if (!donorInfoOnly) {
        if (this.donationForm.amount < this.donationForm.min) {
          this.donationForm.errors['amount'] = this.t("donation.errors.amount_low") + "$" + this.donationForm.min.toFixed(2);
        }
        if (this.donationForm.delayStart && !this.donationForm.delayStartDate) {
          this.donationForm.errors['date'] = this.t("donation.errors.start_date");
        }
        if (!this.donationForm.selectedTarget) {
          this.donationForm.errors['target'] = this.t("donation.errors.generic_req");
        }
      }

      return Object.keys(this.donationForm.errors).length === 0;
    },


    // This is for the 'giving page' form, only allows a single donation at a time
    togglePaymentForm() {
      this.immediateCheckoutDonation = null;
      if (!this.donationForm.loading) {

        if (this.donationSession.loaded) {
          this.donationSession.loaded = false;
        } else {
          this.donationForm.selectedTipOption = 5; // Reset the tip option

          if (this.donationFormValid(false)) {
            const donation = {
              amount: this.dollarsToCents(this.donationForm.amount),
              selectedTarget: this.donationForm.selectedTarget,
              type: this.donationForm.donationType,
              frequency: this.donationForm.frequency,
              delay_start: this.donationForm.delayStart,
              delay_date: this.donationForm.delayStartDate,
            };
            // this.donationCart.push(donation);
            this.singleDonationCheckout(donation);
            this.donationForm.loading = false;
            this.donationSession.loaded = true;
          }
        }
      }
    },


    /** HELPER METHODS **/
    // Email validation function that I definitely understand and didn't rip off stackoverflow
    emailValid() {
      return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/.test(this.donationForm.donorEmail);
    // /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/  <-old one (new one is more generic)
    //   /^[^@\s]+@[^@\s]+\.[^@\s]+$/
    },
    clearFormErrors() {
      this.donationForm.errors = {};
    },
    setDefaultTarget(uuid) {
      this.donationForm.defaultDonatableId = uuid;
    },
    // Converts cent amounts to dollars, from string and decimal values
    dollarsToCents(dollars) {
      try {
        return parseInt((dollars * 100).toFixed(0), 10);
      } catch (error) {
        return 0;
      }
    },
    calculateDefaultCustomTip() {
      this.donationForm.customTipAmount = this.calculatedTip;
    },
  },

  getters: {
    // Returns array of donations being paid for currently
    // Immediate checkout donations will take priority & get cleared out whenever the drawer gets closed
    currentActiveDonations(state) {
      let donations = state.donationCart;
      if (state.immediateCheckoutDonation) {
        donations = [state.immediateCheckoutDonation];
      }
      return donations;
    },

    // Structures the data to display in the summary widget
    donationCartLineItems(state) {
      let data = [];
      let total = 0;

      state.currentActiveDonations.forEach((donation) => {
        total += donation.amount;
        data.push(
          {
            type: donation.type,
            description: donation.type === OneTimeDonation ? "One Time Gift" : donation.frequency + " Gift",
            name: donation.selectedTarget.name,
            frequency: donation.frequency,
            amount: donation.amount,
            image: donation.selectedTarget.banner_image_url ? donation.selectedTarget.banner_image_url : defaultDonatableImage,
            object: donation
          }
        );
      });

      return data;
    },

    // Total of all line items, no fees added
    cartSubtotal(state) {
      let total = 0;

      state.currentActiveDonations.forEach((donation) => {
        total += donation.amount;
      });
      return total;
    },

    cartTipAmount(state) {
      if (!this.showTipOption) {
        return 0; // Dont add tip if the dropdown isn't showing, that's sketchy
      }
      let subTotal = state.cartSubtotal;
      let feeAmount = 0;
      // Custom Tip Amount
      if (state.donationForm.selectedTipOption === "custom") {
        feeAmount = state.donationForm.customTipAmount * 100; // Currency input uses decimals, convert to cents
      } else {
        feeAmount = Math.floor((subTotal * (state.donationForm.selectedTipOption / 100)));
      }

      return Math.ceil(feeAmount);
    },

    // Total of line items w/ fees
    cartTotal(state) {
      return Math.round(state.cartSubtotal + state.cartTipAmount);
    },

    orgLoaded(state) {
      return state.organizationId != null;
    },

    getClientSecret(state) {
      return state.donationSession.client_secret;
    },
    fullName(state) {
      return state.donationForm.fName + " " + state.donationForm.lName;
    },
    showDonationTargetDropdown(state) {
      return !state.donationForm.donationTargets || state.donationForm.donationTargets.length > 1;
    },

    orgProcessorType(state) {
      return state.orgProcessor.type;
    },

    showTipOption(state) {
      return state.orgProcessor.partner_fee_enabled;
    },
    currentDonorUserId(state) {
      return state.donationSession.user_id;
    },
    calculatedTip(state) {
      return (state.cartSubtotal * 0.02) / 100; // 2% default custom tip
    },
  }
});
